import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";

const APP_THEME_KEY = "appTheme";
const LEGACY_THEME_KEYS = [
  "studentDashboardTheme",
  "adminDashboardTheme",
  "schoolDashboardTheme",
];

const AppThemeContext = createContext(null);

const normalizeTheme = (value) => (value === "dark" ? "dark" : "light");

const clearStoredTheme = () => {
  localStorage.removeItem(APP_THEME_KEY);
  LEGACY_THEME_KEYS.forEach((key) => localStorage.removeItem(key));
};

const loadInitialTheme = () => {
  const storedTheme = localStorage.getItem(APP_THEME_KEY);
  if (storedTheme) {
    return normalizeTheme(storedTheme);
  }

  const legacyTheme = LEGACY_THEME_KEYS
    .map((key) => localStorage.getItem(key))
    .find((value) => value === "dark" || value === "light");

  return normalizeTheme(legacyTheme);
};

export const AppThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(loadInitialTheme);
  const skipNextPersistenceRef = useRef(false);
  const isDarkMode = theme === "dark";

  useEffect(() => {
    if (skipNextPersistenceRef.current) {
      skipNextPersistenceRef.current = false;
      clearStoredTheme();
      return;
    }

    localStorage.setItem(APP_THEME_KEY, theme);
    LEGACY_THEME_KEYS.forEach((key) => localStorage.setItem(key, theme));
  }, [theme]);

  useLayoutEffect(() => {
    document.documentElement.classList.toggle("app-theme-dark", isDarkMode);
    document.body.classList.toggle("app-theme-dark", isDarkMode);
    document.documentElement.style.colorScheme = isDarkMode ? "dark" : "light";
  }, [isDarkMode]);

  const setTheme = (nextTheme) => {
    setThemeState((currentTheme) => {
      const resolvedTheme =
        typeof nextTheme === "function" ? nextTheme(currentTheme) : nextTheme;
      return normalizeTheme(resolvedTheme);
    });
  };

  const resetTheme = () => {
    clearStoredTheme();
    skipNextPersistenceRef.current = theme !== "light";
    setThemeState("light");
  };

  const toggleTheme = () => {
    setTheme((currentTheme) => (currentTheme === "dark" ? "light" : "dark"));
  };

  useEffect(() => {
    const handleLogout = () => {
      resetTheme();
    };

    window.addEventListener("auth:logout", handleLogout);
    return () => window.removeEventListener("auth:logout", handleLogout);
  }, []);

  return (
    <AppThemeContext.Provider value={{ theme, isDarkMode, setTheme, toggleTheme, resetTheme }}>
      {children}
    </AppThemeContext.Provider>
  );
};

export const useAppTheme = () => {
  const context = useContext(AppThemeContext);

  if (!context) {
    throw new Error("useAppTheme must be used within an AppThemeProvider");
  }

  return context;
};